// Localization audit: every interface text source, with the screen it appears on
// (LOCALIZATION_AUDIT_PLAN.md, Part 1, 2026-11-03). Admin screens are English-only
// and left out. A new copy block belongs here, or the audit can't see it.
import { STRINGS as BASE_STRINGS } from "@/lib/i18nBase";
import { OVERRIDES } from "@/lib/i18n";
import { UPLOAD_STRINGS, CAT_LABELS, LVL_LABELS } from "@/lib/uploadI18n";
import { bundles as SIGNUP, COUNTRY_LABELS } from "@/lib/signupI18n";
import { REPORT_STRINGS } from "@/lib/reportI18n";
import { STRINGS as LIVE_STRINGS } from "@/lib/liveInterviewI18n";
import { INTERVIEW_MODE_STRINGS } from "@/lib/interviewModeI18n";
import { copy as SESSION_TIMEOUT } from "@/lib/sessionTimeoutI18n";
import { STRINGS as PARTNER_CODE } from "@/lib/partnerCodeI18n";
import { SAMPLE_ROLE_COPY, COPY as RESUME_LANGUAGE } from "@/lib/resumeLanguageI18n";
import { VOICE_SAMPLE_STRINGS } from "@/lib/voiceSampleI18n";
import { BUSY_COPY } from "@/lib/compactUiCopy";
import { BUNDLES as BETA_FEEDBACK } from "@/lib/betaFeedback/i18n";
import { betaReportStrings } from "@/lib/betaReport/i18n";
import { answerScriptStrings } from "@/lib/answerScript/i18n";
import { COPY as ACTIVE_GUARD } from "@/lib/activeInterviewSetupGuard";
import * as HELP_EN from "@/lib/help/articles.en";
import * as HELP_PT from "@/lib/help/articles.pt-BR";
import * as HELP_ES from "@/lib/help/articles.es-419";
import * as HELP_FR from "@/lib/help/articles.fr-CA";
import { CONTACT_COPY } from "@/components/ContactAccountFields";
import { HELP_COPY } from "@/components/HelpAccess";
import { UI as HELP_PANEL } from "@/components/HelpPanel";
import { COPY as LIFECYCLE } from "@/components/InterviewLifecycleControls";
import { CONTACT_NOTICE } from "@/components/PrivacyPolicyBody";
import { COPY as TRANSLATION_AID, DOWNLOAD_COPY } from "@/components/QuestionTranslationAid";
import { SESSION_LIMIT_COPY, CURRENT_QUESTION_COPY, SESSIONS_COPY } from "@/components/SessionAllowanceTracker";
import { DOWNLOAD_LABEL, ACCOUNT_LABEL, SIGN_OUT_LABEL, MENU_LABEL } from "@/components/UserMenu";
import { COPY as VOLUME } from "@/components/VolumeControl";
import { COPY as WARMUP_PLAYBACK } from "@/components/WarmupPlaybackEnhancer";
import { COPY as ACCOUNT } from "@/pages/AccountSettings";
import { STRINGS as AUTH_CALLBACK } from "@/pages/AuthCallback";
import { COPY as DOWNLOADS } from "@/pages/Downloads";
import { COPY as FREE_COMPLETE } from "@/pages/FreeComplete";
import { PRODUCT_TITLES } from "@/pages/Login";
import { STRINGS as PRACTICE_FORMAT } from "@/pages/PracticeFormat";
import { STRINGS as PREPARE } from "@/pages/Prepare";
import { COPY as TRIAL_REPORT } from "@/pages/TrialReport";
import { SETUP_COPY, ACTIVE_INTERVIEW_COPY } from "@/pages/UploadSetup";
import { STRINGS as WAITING_BETA } from "@/pages/WaitingBeta";

const LANGS = ["en", "pt-BR", "es-419", "fr-CA"];
const byLang = (fn) => Object.fromEntries(LANGS.map((l) => [l, fn(l)]));

// The shared dictionaries hold several screens; their key prefix says which.
const PREFIX_SCREENS = {
  "iv.": "Interview (Guided Mode)",
  "rp.": "Report",
  "up.": "Interview Session Setup",
  "jm.": "Interview Session Setup – job match",
  "sen.": "Report – seniority",
  "sessions.": "Sessions used",
  "common.": "Shared buttons and labels",
  "brand.": "Shared buttons and labels",
  "sp.": "Shared buttons and labels",
};

function splitByPrefix(id, copy) {
  const groups = {};
  for (const lang of LANGS) {
    const strings = copy[lang];
    for (const [key, text] of Object.entries(strings || {})) {
      const prefix = Object.keys(PREFIX_SCREENS).find((p) => key.startsWith(p)) || "";
      const screen = PREFIX_SCREENS[prefix] || "Shared buttons and labels";
      groups[screen] ||= {};
      groups[screen][lang] ||= {};
      groups[screen][lang][key] = text;
    }
  }
  return Object.entries(groups).map(([screen, part]) => ({ id, screen, copy: part }));
}

function helpSources() {
  const files = { en: HELP_EN, "pt-BR": HELP_PT, "es-419": HELP_ES, "fr-CA": HELP_FR };
  const text = (a) => ({ title: a.title, body: a.body });
  const sources = [{
    id: "lib/help (categories)",
    screen: "Help – categories",
    copy: byLang((l) => Object.fromEntries((files[l].CATEGORIES || []).map((c) => [c.id, c.title]))),
  }];
  for (const article of HELP_EN.ARTICLES) {
    sources.push({
      id: `lib/help/${article.id}`,
      screen: `Help – ${article.title}`,
      copy: byLang((l) => {
        const found = (files[l].ARTICLES || []).find((a) => a.id === article.id);
        return found ? text(found) : undefined;
      }),
    });
  }
  return sources;
}

export function appSources() {
  return [
    ...splitByPrefix("lib/i18nBase.STRINGS", BASE_STRINGS),
    ...splitByPrefix("lib/i18n.OVERRIDES", OVERRIDES),
    { id: "lib/uploadI18n.UPLOAD_STRINGS", screen: "Interview Session Setup", copy: UPLOAD_STRINGS },
    { id: "lib/uploadI18n.CAT_LABELS", screen: "Interview Session Setup – sample job description", copy: CAT_LABELS },
    { id: "lib/uploadI18n.LVL_LABELS", screen: "Interview Session Setup – sample job description", copy: LVL_LABELS },
    { id: "pages/UploadSetup.SETUP_COPY", screen: "Interview Session Setup", copy: SETUP_COPY },
    { id: "pages/UploadSetup.ACTIVE_INTERVIEW_COPY", screen: "Interview already in progress", copy: ACTIVE_INTERVIEW_COPY },
    { id: "lib/activeInterviewSetupGuard.COPY", screen: "Interview already in progress", copy: ACTIVE_GUARD },
    { id: "lib/resumeLanguageI18n.SAMPLE_ROLE_COPY", screen: "Interview Session Setup – résumé language", copy: SAMPLE_ROLE_COPY },
    { id: "lib/resumeLanguageI18n.COPY", screen: "Interview Session Setup – résumé language", copy: RESUME_LANGUAGE },
    { id: "lib/interviewModeI18n", screen: "Interview Session Setup – practice mode", copy: INTERVIEW_MODE_STRINGS },
    { id: "pages/PracticeFormat.STRINGS", screen: "Practice format", copy: PRACTICE_FORMAT },
    { id: "pages/Prepare.STRINGS", screen: "Preparing the interview", copy: PREPARE },
    { id: "lib/compactUiCopy.BUSY_COPY", screen: "Shared buttons and labels", copy: BUSY_COPY },
    { id: "lib/voiceSampleI18n", screen: "Voice choice", copy: VOICE_SAMPLE_STRINGS },
    { id: "components/WarmupPlaybackEnhancer.COPY", screen: "Interview (Guided Mode) – warm-up", copy: WARMUP_PLAYBACK },
    // Only shown to non-English users, so it has no English.
    { id: "components/QuestionTranslationAid.COPY", screen: "Interview (Guided Mode) – question translation", copy: TRANSLATION_AID, noEnglish: true },
    { id: "components/QuestionTranslationAid.DOWNLOAD_COPY", screen: "Interview (Guided Mode) – question translation", copy: DOWNLOAD_COPY },
    { id: "lib/liveInterviewI18n.STRINGS", screen: "Interview (Advanced Mode)", copy: LIVE_STRINGS },
    { id: "components/InterviewLifecycleControls.COPY", screen: "Interview – Restart / End / Save & Exit", copy: LIFECYCLE },
    { id: "components/VolumeControl.COPY", screen: "Interview – volume", copy: VOLUME },
    { id: "components/SessionAllowanceTracker.SESSION_LIMIT_COPY", screen: "Sessions used", copy: SESSION_LIMIT_COPY },
    { id: "components/SessionAllowanceTracker.CURRENT_QUESTION_COPY", screen: "Sessions used", copy: CURRENT_QUESTION_COPY },
    { id: "components/SessionAllowanceTracker.SESSIONS_COPY", screen: "Sessions used", copy: SESSIONS_COPY },
    { id: "lib/sessionTimeoutI18n.copy", screen: "Session timeout warning", copy: SESSION_TIMEOUT },
    { id: "lib/reportI18n.REPORT_STRINGS", screen: "Report", copy: REPORT_STRINGS },
    { id: "pages/TrialReport.COPY", screen: "Free trial results", copy: TRIAL_REPORT },
    { id: "pages/FreeComplete.COPY", screen: "Free trial complete", copy: FREE_COMPLETE },
    { id: "pages/Downloads.COPY", screen: "My Reports & Downloads", copy: DOWNLOADS },
    { id: "pages/AccountSettings.COPY", screen: "Account Settings", copy: ACCOUNT },
    { id: "components/ContactAccountFields.CONTACT_COPY", screen: "Account Settings – contact", copy: CONTACT_COPY },
    { id: "lib/partnerCodeI18n.STRINGS", screen: "Account Settings – Partner Code", copy: PARTNER_CODE },
    { id: "components/UserMenu", screen: "Account menu", copy: { downloads: DOWNLOAD_LABEL, account: ACCOUNT_LABEL, signOut: SIGN_OUT_LABEL, menu: MENU_LABEL } },
    { id: "lib/signupI18n.bundles", screen: "Sign in / sign up", copy: SIGNUP },
    { id: "lib/signupI18n.COUNTRY_LABELS", screen: "Sign in / sign up", copy: COUNTRY_LABELS },
    { id: "pages/Login.PRODUCT_TITLES", screen: "Sign in / sign up", copy: PRODUCT_TITLES },
    { id: "pages/AuthCallback.STRINGS", screen: "Sign in / sign up", copy: AUTH_CALLBACK },
    { id: "pages/WaitingBeta.STRINGS", screen: "Waiting for beta access", copy: WAITING_BETA },
    { id: "components/PrivacyPolicyBody.CONTACT_NOTICE", screen: "Privacy Policy", copy: CONTACT_NOTICE },
    { id: "lib/betaFeedback/i18n.BUNDLES", screen: "Beta feedback", copy: BETA_FEEDBACK },
    { id: "lib/betaReport/i18n", screen: "Beta Report", copy: byLang(betaReportStrings) },
    { id: "lib/answerScript/i18n", screen: "Answer script", copy: byLang(answerScriptStrings) },
    { id: "components/HelpAccess.HELP_COPY", screen: "Help – button and panel", copy: HELP_COPY },
    { id: "components/HelpPanel.UI", screen: "Help – button and panel", copy: HELP_PANEL },
    ...helpSources(),
  ];
}
